import React, { useEffect, useMemo, useRef } from 'react';
import { AppFormInput, AppFormSearchableSelect } from '../../../components/common/appForms';
import CountryFlag from '../../../components/common/CountryFlag/CountryFlag';
import type { SelectOption } from '../../../components/common/SearchableSelect/SearchableSelect';
import { useCountries } from '../../../constants/countries';
import { useIbgeCities, useIbgeStates } from '../../../hooks/useIbgeLocalities';
import { postalCodeMaxLength, postalCodePlaceholder } from '../../../utils/masks';
import type { CheckoutAddressData } from '../checkout.types';
import { maskCheckoutAddressField } from '../checkout.utils';
import pageStyles from '../CheckoutPage.module.css';

interface AddressStepProps {
  data: CheckoutAddressData;
  errors: Record<string, string>;
  loadingCep?: boolean;
  disabled?: boolean;
  onFieldChange: <K extends keyof CheckoutAddressData>(
    field: K,
    value: CheckoutAddressData[K],
  ) => void;
  onCepBlur: () => void;
}

const sortWithBrazilFirst = <T extends { value: string; label: string }>(
  options: T[],
  brazilValue = 'BR',
): T[] => {
  const sorted = [...options].sort((left, right) => left.label.localeCompare(right.label, 'pt'));
  const brazilIndex = sorted.findIndex((option) => option.value === brazilValue);

  if (brazilIndex > 0) {
    const [brazilOption] = sorted.splice(brazilIndex, 1);
    sorted.unshift(brazilOption);
  }

  return sorted;
};

const formatCountryOption = (option: SelectOption) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <CountryFlag code={option.value} size={16} />
    {option.label}
  </span>
);

const AddressStep: React.FC<AddressStepProps> = ({
  data,
  errors,
  loadingCep = false,
  disabled = false,
  onFieldChange,
  onCepBlur,
}) => {
  const isBrazil = !data.country || data.country === 'BR';
  const { countries: rawCountryOptions } = useCountries();
  const { states: ibgeStateOptions, loading: statesLoading } = useIbgeStates(isBrazil);
  const { cities: ibgeCityOptions, loading: citiesLoading } = useIbgeCities(data.state, isBrazil);
  const prevCountry = useRef(data.country);

  const countryOptions = useMemo(
    () => sortWithBrazilFirst(rawCountryOptions),
    [rawCountryOptions],
  );

  useEffect(() => {
    if (prevCountry.current === data.country) {
      return;
    }

    prevCountry.current = data.country;
    onFieldChange('state', '');
    onFieldChange('city', '');
    onFieldChange('cep', '');
  }, [data.country, onFieldChange]);

  const handleChange = (field: keyof CheckoutAddressData, rawValue: string) => {
    const maskedValue = maskCheckoutAddressField(field, rawValue, data.country);
    onFieldChange(field, maskedValue);
  };

  const handleStateChange = (stateValue: string) => {
    onFieldChange('state', stateValue);
    onFieldChange('city', '');
  };

  const postalCodeLabel = isBrazil ? 'CEP' : 'CEP / Código postal';

  return (
    <div className={pageStyles.formFields}>
      <div className={pageStyles.fieldRowPair}>
        <AppFormSearchableSelect
          id="checkout-country"
          label="País"
          options={countryOptions}
          value={data.country || 'BR'}
          onChange={(event) => onFieldChange('country', event.target.value)}
          placeholder="Brasil"
          searchPlaceholder="Pesquisar"
          noResultsText="País não encontrado"
          disabled={disabled}
          error={errors.country}
          containerClassName={pageStyles.fieldRowItem}
          allowEmpty={false}
          triggerFormat={formatCountryOption}
          optionFormat={formatCountryOption}
        />

        <AppFormInput
          id="checkout-cep"
          name="cep"
          label={postalCodeLabel}
          value={data.cep}
          onChange={(event) => handleChange('cep', event.target.value)}
          onBlur={onCepBlur}
          placeholder={postalCodePlaceholder(data.country || 'BR')}
          maxLength={postalCodeMaxLength(data.country || 'BR')}
          error={errors.cep}
          disabled={disabled || loadingCep}
          containerClassName={pageStyles.fieldRowItem}
        />
      </div>

      <div className={pageStyles.fieldRowStateCity}>
        {isBrazil ? (
          <AppFormSearchableSelect
            id="checkout-state"
            label="Estado"
            options={ibgeStateOptions}
            value={data.state}
            onChange={(event) => handleStateChange(event.target.value)}
            placeholder="Selecione o estado"
            searchPlaceholder="Pesquisar"
            noResultsText="Estado não encontrado"
            disabled={disabled}
            loading={statesLoading}
            error={errors.state}
            containerClassName={pageStyles.fieldRowItem}
          />
        ) : (
          <AppFormInput
            id="checkout-state"
            name="state"
            label="Estado / Província"
            value={data.state}
            onChange={(event) => onFieldChange('state', event.target.value)}
            placeholder="Estado ou província"
            error={errors.state}
            disabled={disabled}
            containerClassName={pageStyles.fieldRowItem}
          />
        )}

        {isBrazil ? (
          <AppFormSearchableSelect
            id="checkout-city"
            label="Cidade"
            options={ibgeCityOptions}
            value={data.city}
            onChange={(event) => onFieldChange('city', event.target.value)}
            placeholder={data.state ? 'Selecione a cidade' : 'Selecione o estado primeiro'}
            searchPlaceholder="Pesquisar"
            noResultsText="Cidade não encontrada"
            disabled={disabled || !data.state}
            loading={citiesLoading}
            error={errors.city}
            containerClassName={pageStyles.fieldRowItem}
          />
        ) : (
          <AppFormInput
            id="checkout-city"
            name="city"
            label="Cidade"
            value={data.city}
            onChange={(event) => onFieldChange('city', event.target.value)}
            placeholder="Digite sua cidade"
            error={errors.city}
            disabled={disabled}
            containerClassName={pageStyles.fieldRowItem}
          />
        )}
      </div>

      <AppFormInput
        id="checkout-neighborhood"
        name="neighborhood"
        label="Bairro"
        value={data.neighborhood}
        onChange={(event) => onFieldChange('neighborhood', event.target.value)}
        placeholder="Digite o bairro"
        error={errors.neighborhood}
        disabled={disabled}
      />

      <div className={pageStyles.fieldRowStreet}>
        <AppFormInput
          id="checkout-street"
          name="street"
          label="Rua"
          value={data.street}
          onChange={(event) => onFieldChange('street', event.target.value)}
          placeholder="Digite o nome da rua"
          error={errors.street}
          disabled={disabled}
          containerClassName={pageStyles.fieldRowItem}
        />

        <AppFormInput
          id="checkout-number"
          name="number"
          label="Número"
          value={data.number}
          onChange={(event) => onFieldChange('number', event.target.value)}
          placeholder="Nº"
          error={errors.number}
          disabled={disabled}
          containerClassName={pageStyles.fieldRowItem}
        />
      </div>

      <AppFormInput
        id="checkout-complement"
        name="complement"
        label="Complemento"
        value={data.complement}
        onChange={(event) => onFieldChange('complement', event.target.value)}
        placeholder="Apto, bloco, etc. (opcional)"
        disabled={disabled}
      />
    </div>
  );
};

export default AddressStep;
